import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaChartLine,
  FaDownload,
  FaPrint,
  FaArrowLeft,
  FaExclamationTriangle,
  FaShoppingCart,
  FaMoneyBillWave,
  FaFileInvoiceDollar,
  FaBalanceScale,
  FaExchangeAlt,
  FaStore,
} from "react-icons/fa";
import { getSalesPurchaseSummary } from "./salesPurchaseSummaryService";
import "./SalesPurchaseSummary.css";
import { usePermissions } from "../../context/PermissionContext";
import AccessDenied from "../ui/AccessDenied";

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtDate = (d) => {
  if (!d) return "-";
  return new Date(d).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const StatusBadge = ({ status }) => {
  const s = (status || "").toLowerCase();
  return <span className={`sps-status ${s}`}>{status || "-"}</span>;
};

const TABS = [
  { key: "sales",    label: "Sales",           icon: <FaStore /> },
  { key: "purchase", label: "Purchase Bills",  icon: <FaShoppingCart /> },
  { key: "combined", label: "Combined View",   icon: <FaBalanceScale /> },
];

const SalesPurchaseSummaryPage = () => {
  const navigate = useNavigate();
  const { canView, canExport } = usePermissions();

  if (!canView("sales_purchase")) {
    return <AccessDenied moduleName="Sales & Purchase Summary" action="view" />;
  }

  // ── Filters ─────────────────────────────────────────────
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // ── Data ────────────────────────────────────────────────
  const [summary, setSummary] = useState(null);
  const [sales, setSales] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [monthly, setMonthly] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── Active tab ───────────────────────────────────────────
  const [activeTab, setActiveTab] = useState("sales");

  const hasFetched = useRef(false);

  // ── Fetch ────────────────────────────────────────────────
  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = {
        start_date: startDate || undefined,
        end_date:   endDate   || undefined,
      };
      const data = await getSalesPurchaseSummary(filters);
      setSummary(data.summary || null);
      setSales(data.sales || []);
      setPurchases(data.purchases || []);
      setMonthly(data.monthly_trend || []);
    } catch (err) {
      console.error("Sales & Purchase Summary error:", err);
      setError("Failed to load the report. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchReport();
    }
  }, [fetchReport]);

  // ── Reset ────────────────────────────────────────────────
  const handleReset = () => {
    setStartDate("");
    setEndDate("");
    hasFetched.current = false;
    setTimeout(() => {
      hasFetched.current = true;
      fetchReport();
    }, 0);
  };

  // ── Export CSV ───────────────────────────────────────────
  const handleExportCSV = () => {
    let rows = [];
    if (activeTab === "sales") {
      rows = [
        ["Invoice #", "Customer", "Date", "Grand Total (AED)", "Paid (AED)", "Balance (AED)", "Status"],
        ...sales.map((s) => [
          s.invoice_no || "-",
          s.customer_name,
          s.sales_date ? s.sales_date.slice(0, 10) : "-",
          s.grand_total,
          s.paid_amount,
          s.balance_amount,
          s.status,
        ]),
      ];
    } else if (activeTab === "purchase") {
      rows = [
        ["Bill #", "Supplier", "Date", "Amount (AED)", "Tax (AED)", "Total (AED)", "Status"],
        ...purchases.map((p) => [
          p.bill_number || "-",
          p.supplier_name,
          p.bill_date ? p.bill_date.slice(0, 10) : "-",
          p.amount,
          p.tax_amount,
          p.total_amount,
          p.status,
        ]),
      ];
    } else {
      rows = [
        ["Month", "Sales (AED)", "Purchases (AED)", "Net (AED)"],
        ...monthly.map((m) => [
          m.month,
          m.total_sales,
          m.total_purchases,
          m.net,
        ]),
      ];
    }

    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sales_purchase_summary_${activeTab}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Max for bar chart
  const maxSales    = monthly.length ? Math.max(...monthly.map((m) => m.total_sales), 1) : 1;
  const maxPurchase = monthly.length ? Math.max(...monthly.map((m) => m.total_purchases), 1) : 1;

  return (
    <div className="sps-page">
      {/* ── Header ─────────────────────────────────────────── */}
      <div className="sps-header">
        <div className="sps-title-group">
          <h2>
            <FaChartLine style={{ color: "#6366f1" }} />
            Sales &amp; Purchase Summary
          </h2>
          <p>Compare sales revenue against purchase costs over any date range</p>
        </div>
        <div className="sps-header-actions">
          <button className="sps-btn sps-btn-export" onClick={handleExportCSV}>
            <FaDownload /> Export CSV
          </button>
          <button className="sps-btn sps-btn-print" onClick={() => window.print()}>
            <FaPrint /> Print
          </button>
          <button className="sps-btn sps-btn-back" onClick={() => navigate("/dashboard")}>
            <FaArrowLeft /> Dashboard
          </button>
        </div>
      </div>

      {/* ── Summary Cards ───────────────────────────────────── */}
      {summary && (
        <div className="sps-summary-grid">
          <div className="sps-card sales-rev">
            <div className="sps-card-icon" style={{ color: "#10b981" }}>
              <FaMoneyBillWave />
            </div>
            <div className="sps-card-label">Total Sales Revenue</div>
            <div className="sps-card-value">AED {fmt(summary.total_sales_revenue)}</div>
            <div className="sps-card-sub">{summary.sales_count} invoices</div>
          </div>

          <div className="sps-card sales-cost">
            <div className="sps-card-icon" style={{ color: "#f59e0b" }}>
              <FaExchangeAlt />
            </div>
            <div className="sps-card-label">Total Sales Paid</div>
            <div className="sps-card-value">AED {fmt(summary.total_sales_paid)}</div>
            <div className="sps-card-sub">AED {fmt(summary.total_sales_balance)} outstanding</div>
          </div>

          <div className="sps-card sales-prof">
            <div className="sps-card-icon" style={{ color: "#6366f1" }}>
              <FaChartLine />
            </div>
            <div className="sps-card-label">Total Profit</div>
            <div className="sps-card-value">AED {fmt(summary.total_profit)}</div>
            <div className="sps-card-sub">{summary.margin_pct}% margin</div>
          </div>

          <div className="sps-card purch-tot">
            <div className="sps-card-icon" style={{ color: "#ef4444" }}>
              <FaFileInvoiceDollar />
            </div>
            <div className="sps-card-label">Total Purchases</div>
            <div className="sps-card-value">AED {fmt(summary.total_purchases)}</div>
            <div className="sps-card-sub">{summary.purchase_count} bills</div>
          </div>

          <div className="sps-card net-bal">
            <div className="sps-card-icon" style={{ color: "#0ea5e9" }}>
              <FaBalanceScale />
            </div>
            <div className="sps-card-label">Net Balance</div>
            <div
              className="sps-card-value"
              style={{ color: summary.net_balance >= 0 ? "#16a34a" : "#dc2626" }}
            >
              AED {fmt(summary.net_balance)}
            </div>
            <div className="sps-card-sub">Revenue − Purchases</div>
          </div>

          <div className="sps-card txn-count">
            <div className="sps-card-icon" style={{ color: "#8b5cf6" }}>
              <FaShoppingCart />
            </div>
            <div className="sps-card-label">Total Transactions</div>
            <div className="sps-card-value">
              {(summary.sales_count || 0) + (summary.purchase_count || 0)}
            </div>
            <div className="sps-card-sub">
              {summary.sales_count} sales + {summary.purchase_count} purchases
            </div>
          </div>
        </div>
      )}

      {/* ── Filters ─────────────────────────────────────────── */}
      <div className="sps-filters">
        <div className="sps-filter-group">
          <label>Date From</label>
          <input
            id="sps-start-date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="sps-filter-group">
          <label>Date To</label>
          <input
            id="sps-end-date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <button id="sps-apply-btn" className="sps-btn sps-btn-filter" onClick={fetchReport}>
          Apply Filters
        </button>
        <button id="sps-reset-btn" className="sps-btn sps-btn-reset" onClick={handleReset}>
          Reset
        </button>
      </div>

      {/* ── Loading / Error ──────────────────────────────────── */}
      {loading && (
        <div className="sps-loading">
          <div className="sps-spinner" />
          Loading report…
        </div>
      )}

      {error && (
        <div className="sps-error">
          <FaExclamationTriangle /> {error}
        </div>
      )}

      {!loading && !error && (
        <>
          {/* ── Tabs ───────────────────────────────────────────── */}
          <div className="sps-tabs">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                className={`sps-tab ${tab.key}${activeTab === tab.key ? " active" : ""}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>

          {/* ── Sales Tab ──────────────────────────────────────── */}
          {activeTab === "sales" && (
            <div className="sps-table-card">
              <div className="sps-table-header">
                <strong>Sales Invoices</strong>
                <span>{sales.length} record{sales.length !== 1 ? "s" : ""}</span>
              </div>
              {sales.length === 0 ? (
                <div className="sps-empty">
                  <div className="sps-empty-icon"><FaStore /></div>
                  No sales found for the selected period.
                </div>
              ) : (
                <div className="sps-table-wrapper">
                  <table className="sps-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Invoice #</th>
                        <th>Customer</th>
                        <th>Date</th>
                        <th className="num">Grand Total (AED)</th>
                        <th className="num">Paid (AED)</th>
                        <th className="num">Balance (AED)</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sales.map((s, idx) => (
                        <tr key={s.id}>
                          <td style={{ color: "#94a3b8", fontSize: "0.78rem" }}>{idx + 1}</td>
                          <td style={{ fontWeight: 600, color: "#6366f1" }}>
                            {s.invoice_no || "-"}
                          </td>
                          <td>{s.customer_name || "-"}</td>
                          <td>{fmtDate(s.sales_date)}</td>
                          <td className="num">
                            <span className="sps-positive">{fmt(s.grand_total)}</span>
                          </td>
                          <td className="num">{fmt(s.paid_amount)}</td>
                          <td className="num">
                            <span className={s.balance_amount > 0 ? "sps-negative" : "sps-positive"}>
                              {fmt(s.balance_amount)}
                            </span>
                          </td>
                          <td><StatusBadge status={s.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={4}><strong>Total</strong></td>
                        <td className="num">
                          <span className="sps-positive">
                            {fmt(sales.reduce((a, s) => a + Number(s.grand_total || 0), 0))}
                          </span>
                        </td>
                        <td className="num">
                          {fmt(sales.reduce((a, s) => a + Number(s.paid_amount || 0), 0))}
                        </td>
                        <td className="num">
                          {fmt(sales.reduce((a, s) => a + Number(s.balance_amount || 0), 0))}
                        </td>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── Purchase Tab ───────────────────────────────────── */}
          {activeTab === "purchase" && (
            <div className="sps-table-card">
              <div className="sps-table-header">
                <strong>Purchase Bills</strong>
                <span>{purchases.length} record{purchases.length !== 1 ? "s" : ""}</span>
              </div>
              {purchases.length === 0 ? (
                <div className="sps-empty">
                  <div className="sps-empty-icon"><FaShoppingCart /></div>
                  No purchase bills found for the selected period.
                </div>
              ) : (
                <div className="sps-table-wrapper">
                  <table className="sps-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Bill #</th>
                        <th>Supplier</th>
                        <th>Date</th>
                        <th>Due Date</th>
                        <th className="num">Amount (AED)</th>
                        <th className="num">Tax (AED)</th>
                        <th className="num">Total (AED)</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {purchases.map((p, idx) => (
                        <tr key={p.id}>
                          <td style={{ color: "#94a3b8", fontSize: "0.78rem" }}>{idx + 1}</td>
                          <td style={{ fontWeight: 600, color: "#ef4444" }}>
                            {p.bill_number || "-"}
                          </td>
                          <td>{p.supplier_name || "-"}</td>
                          <td>{fmtDate(p.bill_date)}</td>
                          <td>{fmtDate(p.due_date)}</td>
                          <td className="num">{fmt(p.amount)}</td>
                          <td className="num">{fmt(p.tax_amount)}</td>
                          <td className="num">
                            <span className="sps-negative">{fmt(p.total_amount)}</span>
                          </td>
                          <td><StatusBadge status={p.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={5}><strong>Total</strong></td>
                        <td className="num">
                          {fmt(purchases.reduce((a, p) => a + Number(p.amount || 0), 0))}
                        </td>
                        <td className="num">
                          {fmt(purchases.reduce((a, p) => a + Number(p.tax_amount || 0), 0))}
                        </td>
                        <td className="num">
                          <span className="sps-negative">
                            {fmt(purchases.reduce((a, p) => a + Number(p.total_amount || 0), 0))}
                          </span>
                        </td>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── Combined Tab ───────────────────────────────────── */}
          {activeTab === "combined" && (
            <div className="sps-table-card">
              <div className="sps-table-header">
                <strong>Monthly Trend — Sales vs Purchases</strong>
                <span>{monthly.length} month{monthly.length !== 1 ? "s" : ""}</span>
              </div>
              {monthly.length === 0 ? (
                <div className="sps-empty">
                  <div className="sps-empty-icon"><FaBalanceScale /></div>
                  No data available for the selected period.
                </div>
              ) : (
                <div className="sps-table-wrapper">
                  <table className="sps-table">
                    <thead>
                      <tr>
                        <th>Month</th>
                        <th className="num">Sales (AED)</th>
                        <th style={{ width: "180px" }}>Sales Bar</th>
                        <th className="num">Purchases (AED)</th>
                        <th style={{ width: "180px" }}>Purchase Bar</th>
                        <th className="num">Net (AED)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthly.map((m) => (
                        <tr key={m.month}>
                          <td style={{ fontWeight: 600 }}>{m.month}</td>
                          <td className="num">
                            <span className="sps-positive">{fmt(m.total_sales)}</span>
                          </td>
                          <td>
                            <div className="sps-bar-wrap">
                              <div className="sps-bar-bg">
                                <div
                                  className="sps-bar-fill-sales"
                                  style={{
                                    width: `${Math.round((m.total_sales / maxSales) * 100)}%`,
                                  }}
                                />
                              </div>
                              <span className="sps-bar-label">
                                {Math.round((m.total_sales / maxSales) * 100)}%
                              </span>
                            </div>
                          </td>
                          <td className="num">
                            <span className="sps-negative">{fmt(m.total_purchases)}</span>
                          </td>
                          <td>
                            <div className="sps-bar-wrap">
                              <div className="sps-bar-bg">
                                <div
                                  className="sps-bar-fill-purchase"
                                  style={{
                                    width: `${Math.round((m.total_purchases / maxPurchase) * 100)}%`,
                                  }}
                                />
                              </div>
                              <span className="sps-bar-label">
                                {Math.round((m.total_purchases / maxPurchase) * 100)}%
                              </span>
                            </div>
                          </td>
                          <td className="num">
                            <span className={m.net >= 0 ? "sps-positive" : "sps-negative"}>
                              {fmt(m.net)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td><strong>Grand Total</strong></td>
                        <td className="num">
                          <span className="sps-positive">
                            {fmt(monthly.reduce((a, m) => a + Number(m.total_sales || 0), 0))}
                          </span>
                        </td>
                        <td />
                        <td className="num">
                          <span className="sps-negative">
                            {fmt(monthly.reduce((a, m) => a + Number(m.total_purchases || 0), 0))}
                          </span>
                        </td>
                        <td />
                        <td className="num">
                          <span
                            className={
                              monthly.reduce((a, m) => a + Number(m.net || 0), 0) >= 0
                                ? "sps-positive"
                                : "sps-negative"
                            }
                          >
                            {fmt(monthly.reduce((a, m) => a + Number(m.net || 0), 0))}
                          </span>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SalesPurchaseSummaryPage;
